import { useState, useEffect } from "react";
import { API } from "../../api";

/**
 * Admin-only: SendGrid email delivery health dashboard.
 * Shows totals (sent / delivered / deferred / bounced / blocked) + per-day breakdown
 * for the last N days. Useful for spotting deliverability issues without leaving Aura.
 */
export function AdminEmailHealthModal({ onClose }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`${API}/admin/email-health?days=${days}`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Failed to load");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [days]);

  const totals = data?.totals || {};
  const health = data?.health || {};

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="payment-modal admin-modal" onClick={(e) => e.stopPropagation()} data-testid="admin-email-health-modal">
        <div className="modal-icon">📬</div>
        <h2>Email delivery health</h2>
        <p className="modal-subtitle">
          From <strong>{data?.from_email || "—"}</strong> · last {days} days
        </p>

        <div className="email-health-window-picker">
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              className={`window-pill ${days === d ? "active" : ""}`}
              onClick={() => setDays(d)}
              data-testid={`email-health-window-${d}`}
            >
              {d === 1 ? "24h" : `${d}d`}
            </button>
          ))}
        </div>

        {loading && <div className="email-health-loading">Loading SendGrid stats…</div>}
        {error && <div className="error-message">{error}</div>}
        {data && !data.configured && (
          <div className="error-message">
            SendGrid not configured ({data.reason || "no API key"}).
          </div>
        )}
        {data && data.configured && !data.ok && (
          <div className="error-fallback">
            <div className="error-message" data-testid="email-health-error">{data.error || "Couldn't reach SendGrid"}</div>
            {data.local_log && (
              <div className="local-log-summary">
                <p className="local-log-headline">
                  Local activity (from Aura&apos;s side):
                </p>
                <div className="email-health-headline-row">
                  <HealthStat label="Sends attempted" value={data.local_log.total_sends ?? 0} tone="neutral" sub="from Aura" />
                  <HealthStat label="Accepted" value={data.local_log.accepted_by_sendgrid ?? 0} tone="good" sub="by SendGrid" />
                  <HealthStat label="Rejected" value={data.local_log.failed_at_sendgrid ?? 0} tone={(data.local_log.failed_at_sendgrid ?? 0) > 0 ? "bad" : "neutral"} sub="at the gate" />
                </div>
              </div>
            )}
            {data.recent_log?.length > 0 && (
              <div className="email-health-perday">
                <div className="email-health-perday-header">
                  <span>When</span>
                  <span>To</span>
                  <span>Subject</span>
                  <span>Status</span>
                </div>
                {data.recent_log.slice(0, 10).map((r, i) => (
                  <div className="email-health-perday-row" key={i}>
                    <span>{(r.sent_at || "").slice(5, 16).replace("T", " ")}</span>
                    <span title={r.to}>{(r.to || "").split("@")[0]}@…</span>
                    <span title={r.subject}>{(r.subject || "").slice(0, 22)}…</span>
                    <span className={r.ok ? "" : "bad"}>{r.ok ? `✓ ${r.status_code}` : `✗ ${r.status_code}`}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {data?.ok && (
          <>
            <div className="email-health-headline-row">
              <HealthStat label="Delivered" value={`${health.delivered_pct ?? 0}%`} tone="good" sub={`${totals.delivered ?? 0} emails`} />
              <HealthStat label="Deferred" value={`${health.deferred_pct ?? 0}%`} tone={(health.deferred_pct ?? 0) > 20 ? "warn" : "neutral"} sub={`${totals.deferred ?? 0} (will retry)`} />
              <HealthStat label="Bounced" value={`${health.bounce_pct ?? 0}%`} tone={(health.bounce_pct ?? 0) > 2 ? "bad" : "neutral"} sub={`${totals.bounces ?? 0} hard-rejected`} />
            </div>

            <div className="email-health-secondary-row">
              <SecondaryStat label="Requests" value={totals.requests ?? 0} />
              <SecondaryStat label="Opens" value={totals.opens ?? 0} />
              <SecondaryStat label="Clicks" value={totals.clicks ?? 0} />
              <SecondaryStat label="Spam reports" value={totals.spam_reports ?? 0} tone={(totals.spam_reports ?? 0) > 0 ? "bad" : "neutral"} />
            </div>

            {data.per_day?.length > 0 && (
              <div className="email-health-perday">
                <div className="email-health-perday-header">
                  <span>Date</span>
                  <span>Delivered</span>
                  <span>Deferred</span>
                  <span>Bounced</span>
                </div>
                {data.per_day.slice().reverse().slice(0, 10).map((d) => (
                  <div className="email-health-perday-row" key={d.date}>
                    <span>{d.date}</span>
                    <span>{d.delivered}</span>
                    <span className={d.deferred > 0 ? "warn" : ""}>{d.deferred}</span>
                    <span className={d.bounces > 0 ? "bad" : ""}>{d.bounces}</span>
                  </div>
                ))}
              </div>
            )}

            <p className="email-health-tip">
              💡 If deliveries are stuck high in <strong>Deferred</strong>, that means recipient servers
              (like Yahoo) are throttling — SendGrid retries automatically over 72 hours.
              If <strong>Bounced</strong> is consistently &gt; 2%, double-check SPF and DKIM records.
            </p>
          </>
        )}

        <button className="ghost-btn small" onClick={onClose} data-testid="close-email-health-btn">
          Close
        </button>
      </div>
    </div>
  );
}

function HealthStat({ label, value, sub, tone = "neutral" }) {
  return (
    <div className={`health-stat health-stat-${tone}`}>
      <div className="health-stat-label">{label}</div>
      <div className="health-stat-value">{value}</div>
      <div className="health-stat-sub">{sub}</div>
    </div>
  );
}

function SecondaryStat({ label, value, tone = "neutral" }) {
  return (
    <div className={`health-stat-secondary health-stat-${tone}`}>
      <div className="health-stat-value-sm">{value}</div>
      <div className="health-stat-label-sm">{label}</div>
    </div>
  );
}
